import React from 'react';
import { Link } from 'react-router-dom';
import { useSortable } from '@dnd-kit/sortable';
import {
  GripVertical,
  MoreVertical,
  Pencil,
  Trash2,
  Globe,
  Lock,
  Layers,
  FileText,
  ExternalLink,
  Loader2,
} from 'lucide-react';
import type { LearnTopic } from '@/client/types/learn';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { getLearnIconComponent } from './LearnIconPicker';

interface SortableTopicCardProps {
  topic: LearnTopic;
  onEdit: (topic: LearnTopic) => void;
  onDelete: (topic: LearnTopic) => void;
  onTogglePublish: (topic: LearnTopic) => void;
  isPublishing?: boolean;
}

export const SortableTopicCard: React.FC<SortableTopicCardProps> = ({
  topic,
  onEdit,
  onDelete,
  onTogglePublish,
  isPublishing,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: topic.id });

  const style: React.CSSProperties = {
    transform: transform
      ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
      : undefined,
    transition,
    zIndex: isDragging ? 50 : undefined,
    opacity: isDragging ? 0.6 : 1,
  };

  const IconComp = getLearnIconComponent(topic.icon);
  const isMonitored = topic.kind === 'monitored';
  const isPublished = Boolean(topic.published);
  const sectionCount = topic.section_count ?? topic.sections?.length ?? 0;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`group relative flex flex-col justify-between rounded-xl border bg-white p-5 shadow-sm transition-all hover:shadow-md ${
        isDragging
          ? 'border-blue-400 ring-2 ring-blue-100'
          : 'border-gray-200 hover:border-gray-300'
      }`}
    >
      <div>
        {/* Top bar: Drag handle, Icon, Title, Status & Dropdown */}
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <button
              type="button"
              {...attributes}
              {...listeners}
              className="cursor-grab text-gray-400 hover:text-gray-600 active:cursor-grabbing p-0.5 rounded touch-none"
              title="Drag to reorder topics"
            >
              <GripVertical className="h-4 w-4" />
            </button>
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-700">
              <IconComp className="h-5 w-5" />
            </div>
            <div>
              <Link
                to={`/admin/content/learn/topics/${encodeURIComponent(topic.id)}`}
                className="font-semibold text-gray-900 hover:text-blue-600 transition-colors line-clamp-1"
              >
                {topic.title}
              </Link>
              <span className="text-[11px] font-mono text-gray-400">/{topic.id}</span>
            </div>
          </div>

          <div className="flex items-center gap-1">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8 text-gray-500">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-44">
                <DropdownMenuItem asChild>
                  <Link
                    to={`/admin/content/learn/topics/${encodeURIComponent(topic.id)}`}
                    className="flex items-center gap-2 cursor-pointer"
                  >
                    <ExternalLink className="h-4 w-4" />
                    <span>View Sections</span>
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => onEdit(topic)}
                  className="flex items-center gap-2 cursor-pointer"
                >
                  <Pencil className="h-4 w-4" />
                  <span>Edit Topic</span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => onTogglePublish(topic)}
                  disabled={isPublishing}
                  className="flex items-center gap-2 cursor-pointer"
                >
                  {isPublishing ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Globe className="h-4 w-4" />
                  )}
                  <span>{isPublished ? 'Unpublish' : 'Publish'}</span>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => onDelete(topic)}
                  className="flex items-center gap-2 text-red-600 focus:text-red-600 cursor-pointer"
                >
                  <Trash2 className="h-4 w-4" />
                  <span>Delete</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Description */}
        <p className="mt-3 text-xs leading-relaxed text-gray-600 line-clamp-2 min-h-[2rem]">
          {topic.description || <span className="text-gray-400 italic">No description provided</span>}
        </p>
      </div>

      {/* Badges & Footer */}
      <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-1.5 flex-wrap">
          {isPublished ? (
            <Badge variant="outline" className="border-emerald-200 bg-emerald-50 text-emerald-700 text-[10px] font-medium">
              Published
            </Badge>
          ) : (
            <Badge variant="outline" className="border-gray-200 bg-gray-50 text-gray-600 text-[10px] font-medium">
              Draft
            </Badge>
          )}

          {isMonitored ? (
            <Badge variant="outline" className="border-amber-200 bg-amber-50 text-amber-700 text-[10px] font-medium flex items-center gap-1">
              <Lock className="h-2.5 w-2.5" /> Monitored
            </Badge>
          ) : (
            <Badge variant="outline" className="border-blue-200 bg-blue-50 text-blue-700 text-[10px] font-medium flex items-center gap-1">
              <Globe className="h-2.5 w-2.5" /> Open
            </Badge>
          )}

          <Badge variant="secondary" className="text-[10px] text-gray-600 capitalize">
            {topic.surface || 'reference'}
          </Badge>
        </div>

        <Link
          to={`/admin/content/learn/topics/${encodeURIComponent(topic.id)}`}
          className="flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700 transition-colors"
        >
          <FileText className="h-3.5 w-3.5" />
          <span>{sectionCount} {sectionCount === 1 ? 'section' : 'sections'}</span>
        </Link>
      </div>
    </div>
  );
};
